export interface JsonChunkOptions {
  pretty?: boolean;
  canonical?: boolean;
  strict?: boolean;
  chunkCharacters?: number;
  /** Internal persistence adapter. Default JSON behavior is unchanged. */
  sequence?: (value: object) => Iterable<unknown> | undefined;
}

/** JSON tokens, packed without a project-sized string. Canonical mode sorts keys,
 * preserving JSON value equality independently of object insertion order. */
export function* jsonChunks(
  value: unknown,
  options: JsonChunkOptions = {},
): Generator<string> {
  const { pretty = false, canonical = false, strict = false } = options;
  const requestedLimit = options.chunkCharacters ?? 1024 * 1024;
  if (!Number.isSafeInteger(requestedLimit) || requestedLimit < 1)
    throw new RangeError('JSON chunk size must be a positive integer');
  const limit = Math.max(16, requestedLimit);
  const ancestors = new Set<object>();
  function prepare(input: unknown, key: string): unknown {
    if (
      input &&
      (typeof input === 'object' || typeof input === 'function') &&
      'toJSON' in input &&
      typeof input.toJSON === 'function'
    )
      input = input.toJSON(key);
    if (input instanceof Number || input instanceof String || input instanceof Boolean)
      input = input.valueOf();
    if (
      input &&
      typeof input === 'object' &&
      Object.prototype.toString.call(input) === '[object BigInt]'
    )
      input = BigInt.prototype.valueOf.call(input);
    if (strict && (typeof input === 'function' || typeof input === 'symbol'))
      throw new Error('Project state must be serializable.');
    return input;
  }
  const omitted = (input: unknown) =>
    input === undefined || typeof input === 'function' || typeof input === 'symbol';
  // Keep traversal in one generator. Forwarding every scalar through one
  // generator per ancestor dominated runtime-graph save comparisons.
  function* tokens(root: unknown): Generator<string> {
    interface Frame {
      value: Record<string, unknown> | unknown[];
      keys?: string[];
      index: number;
      count: number;
      indent: string;
      closeIndent: string;
      iterator?: Iterator<unknown>;
    }
    const stack: Frame[] = [];
    let input = root;
    try {
      while (true) {
        if (!input || typeof input !== 'object') {
          yield JSON.stringify(input) ?? 'null';
        } else {
          if (ancestors.has(input))
            throw new TypeError('Converting circular structure to JSON');
          ancestors.add(input);
          const sequence = options.sequence?.(input);
          const array = !!sequence || Array.isArray(input);
          const frame: Frame = {
            value: input as Frame['value'],
            index: 0,
            count: 0,
            indent: pretty ? '\n' + '  '.repeat(stack.length + 1) : '',
            closeIndent: pretty ? '\n' + '  '.repeat(stack.length) : '',
            iterator: sequence?.[Symbol.iterator](),
          };
          stack.push(frame);
          yield array ? '[' : '{';
          // Enumerate after the opening token, matching chunk-boundary timing.
          if (!array) {
            frame.keys = Object.keys(input);
            if (canonical) frame.keys.sort();
          }
        }
        let foundChild = false;
        while (stack.length) {
          const frame = stack[stack.length - 1];
          const array = !frame.keys;
          const next = frame.iterator?.next();
          if (
            frame.iterator
              ? !next!.done
              : frame.index < (frame.keys?.length ?? (frame.value as unknown[]).length)
          ) {
            const key = frame.keys ? frame.keys[frame.index++] : String(frame.index++);
            const child = prepare(
              frame.iterator
                ? next!.value
                : (frame.value as Record<string, unknown>)[key],
              key,
            );
            if (!array && omitted(child)) continue;
            yield (frame.count++ ? ',' : '') +
              frame.indent +
              (array ? '' : JSON.stringify(key) + (pretty ? ': ' : ':'));
            input = omitted(child) ? null : child;
            foundChild = true;
            break;
          }
          yield (frame.count ? frame.closeIndent : '') + (array ? ']' : '}');
          ancestors.delete(frame.value);
          stack.pop();
        }
        if (!foundChild) return;
      }
    } finally {
      // An outer comparator/writer may stop while a sequence cursor is active.
      let cleanupError: unknown;
      let cleanupFailed = false;
      for (const frame of stack.reverse()) {
        try {
          frame.iterator?.return?.();
        } catch (error) {
          if (!cleanupFailed) cleanupError = error;
          cleanupFailed = true;
        }
      }
      if (cleanupFailed) throw cleanupError;
    }
  }
  const root = prepare(value, '');
  if (omitted(root)) throw new TypeError('Value cannot be exported as JSON');
  let fragments: string[] = [];
  let length = 0;
  for (const token of tokens(root)) {
    let offset = 0;
    while (offset < token.length) {
      let take = Math.min(limit - length, token.length - offset);
      // Keep UTF-16 pairs together when the chunks are independently UTF-8 encoded.
      if (
        take &&
        length + take === limit &&
        token.charCodeAt(offset + take - 1) >= 0xd800 &&
        token.charCodeAt(offset + take - 1) <= 0xdbff
      )
        take--;
      if (take) {
        fragments.push(token.slice(offset, offset + take));
        length += take;
        offset += take;
      }
      if (length === limit || !take) {
        yield fragments.join('');
        fragments = [];
        length = 0;
      }
    }
  }
  if (length) yield fragments.join('');
}

/** Exact comparison, including differently packed chunks; no hashes or giant joins. */
export function equalJsonChunks(a: readonly string[], b: readonly string[]): boolean {
  let ai = 0,
    bi = 0,
    ao = 0,
    bo = 0;
  while (true) {
    while (ai < a.length && !a[ai].length) ai++;
    while (bi < b.length && !b[bi].length) bi++;
    if (ai === a.length || bi === b.length) break;
    const length = Math.min(a[ai].length - ao, b[bi].length - bo);
    if (a[ai].slice(ao, ao + length) !== b[bi].slice(bo, bo + length)) return false;
    ao += length;
    bo += length;
    if (ao === a[ai].length) {
      ai++;
      ao = 0;
    }
    if (bo === b[bi].length) {
      bi++;
      bo = 0;
    }
  }
  return ai === a.length && bi === b.length;
}
